/* Build in libraries */

/* Third party libraries */
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
/* Components */
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import LoadingNotificationPreferences from '@/components/UI/loading/LoadingNotificationPreferences';
import { SelectAll } from '@/pages/Settings/NotificationPreferences/SelectAll';
import type { INotificationPreferencesNavItems } from '@/pages/Settings/NotificationPreferences/types';
/* Services */
import { getNotificationPreferences, putNotificationPreferences, putNotificationPreferencesDefault } from '../../../api/Notification';
import type { IGetNotificationPreferencesServiceResData, IPutNotificationPreferencesPayload } from '../../../api/Notification/types';
/* Assets and  Miscellaneous */
import { ReactQueryKey } from '../../../constants/ReactQueryKeys';
import { useCurrentAccountData } from '../../../hooks/ReactQueryHooks/useCurrentAccountQuery';
import { notifyUser } from '../../../store/Toast';
import { sanitizeResponseError } from '../../../utils';
import { convertNotificationPreferencesToFormState, createPutPayload } from './helper';
import NotificationPreferencesGroup from './NotificationPreferencesGroup';

function NotificationPreferencesHookForm({
  data,
  forceRender,
}: {
  data: IGetNotificationPreferencesServiceResData[];
  forceRender: () => unknown;
}) {
  const {
    reset,
    getValues,
    formState: { isDirty },
  } = useFormContext();
  const [activeNavItem, setActiveNavItem] = useState<string>('Terminal');

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  let notificationNavItems: INotificationPreferencesNavItems[] = [
    { name: 'Terminal', slug: 'TERMINAL' },
    { name: 'Sherpa', slug: 'SHERPA' },
    { name: 'Rover', slug: 'ROVER' },
  ];
  const fetchCurrentAccount = useCurrentAccountData({});
  const currentAccount = fetchCurrentAccount?.data?.data;

  if (currentAccount?.subdomain === 'sageadmin') {
    notificationNavItems = notificationNavItems.filter((nvi) => nvi.slug === 'TERMINAL');
  }

  let currentNavNotificationPreferences = data?.filter(
    (item: IGetNotificationPreferencesServiceResData) => item?.notificationFor === activeNavItem
  );
  currentNavNotificationPreferences = currentNavNotificationPreferences?.filter(
    (f) =>
      f.notificationGroup !== 'Partner Proposal' &&
      f.notificationGroup !== 'Client Application' &&
      f.notificationGroup !== 'Application Event'
  );

  const saveNotificationPreferences = useMutation((data: IPutNotificationPreferencesPayload[]) => putNotificationPreferences({ data }), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKey.GET_NOTIFICATION_PREFERENCES] });
      reset(getValues());
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Notification Preferences saved successfully',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const setDefaultNotificationPreferences = useMutation(() => putNotificationPreferencesDefault(), {
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKey.GET_NOTIFICATION_PREFERENCES] });
      forceRender();
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Notification Preferences set to default successfully',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  return (
    <HookForm
      noDefaultClass
      onSubmit={(values) => {
        const payload = createPutPayload(values);
        saveNotificationPreferences.mutate(payload);
      }}
    >
      <div className='p-8'>
        <nav className='flex gap-8 border-b-2'>
          {notificationNavItems.map((item: INotificationPreferencesNavItems) => (
            <p
              key={item.slug}
              className={`hover:text-primaryColor-normal cursor-pointer hover:border-primaryColor-normal ${
                activeNavItem === item.name && 'border-primaryColor-normal text-primaryColor-normal border-b-2 pb-2'
              }`}
              onClick={() => setActiveNavItem(item.name)}
            >
              {item.name}
            </p>
          ))}
        </nav>

        <div className='flex justify-between py-6 border-b-2 items-center sticky -top-11 bg-white'>
          <h4 className='text-base'>
            {activeNavItem === 'Terminal'
              ? 'Overall System Notification Preference'
              : activeNavItem === 'Sherpa'
                ? 'Sherpa Application Notification Preference'
                : activeNavItem === 'Rover'
                  ? 'Rover Application Notification Preference'
                  : false}
          </h4>
          <div className='flex gap-4'>
            {isDirty && (
              <>
                <Button label='Cancel Changes' btnStyle='btn-neutral' onClick={() => reset()} />
                <Button label='Save Changes' btnStyle='btn-primary' type='submit' disabled={saveNotificationPreferences.isLoading} />
              </>
            )}
            <Button
              label='Set to Default'
              btnStyle='btn-neutral'
              icon={<Bars3Icon className='h-5 w-5' />}
              disabled={setDefaultNotificationPreferences.isLoading}
              onClick={setDefaultNotificationPreferences.mutate}
            />
          </div>
        </div>
        <SelectAll key={activeNavItem} activeNavItem={activeNavItem} />
        {currentNavNotificationPreferences?.map((ntf) => (
          <NotificationPreferencesGroup key={ntf.notificationGroup} currentTab={activeNavItem} data={ntf} />
        ))}
      </div>
    </HookForm>
  );
}

export default function NotificationPreferences() {
  const [key, setKey] = useState(true);
  const fetchNotificationPreferences = useQuery({
    queryKey: [ReactQueryKey.GET_NOTIFICATION_PREFERENCES],
    queryFn: () => getNotificationPreferences(),
  });
  const notificationPreferences = fetchNotificationPreferences?.data?.data;

  if (fetchNotificationPreferences.isLoading) return <LoadingNotificationPreferences />;

  if (notificationPreferences) {
    const { initialState } = convertNotificationPreferencesToFormState(notificationPreferences);

    return (
      <HookFormProvider mode='all' defaultValues={initialState} key={Number(key)}>
        <NotificationPreferencesHookForm data={notificationPreferences} forceRender={() => setKey(!key)} />
      </HookFormProvider>
    );
  }

  return null;
}
